import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useAuth } from '../hooks/useAuth';
import Badge from '../components/shared/Badge';
import Pagination from '../components/shared/Pagination';
import ConfirmModal from '../components/shared/ConfirmModal';
import JobDrawer from '../components/jobs/JobDrawer';
import { format } from 'date-fns';

const SORT_COLS = {
  no: 'j.job_no', date: 'j.created_at', client: 'c.name',
  proj: 'j.project_name', cat: 'cat.name', due: 'j.due_date',
  designer: 'u.name', status: 's.name', mode: 'j.mode'
};

export default function Jobs() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('DESC');
  const [filters, setFilters] = useState({ search: '', status_id: '', designer_id: '', client_id: '', mode: '', date_from: '', date_to: '' });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editJob, setEditJob] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [archiveId, setArchiveId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['jobs', page, perPage, sortCol, sortDir, filters],
    queryFn: () => api.get('/jobs', { params: {
      ...filters, page, per_page: perPage,
      sort_col: SORT_COLS[sortCol], sort_dir: sortDir
    }}).then(r => r.data),
    placeholderData: keepPreviousData,
  });

  const { data: kpis } = useQuery({
    queryKey: ['kpis'],
    queryFn: () => api.get('/jobs/meta/kpis').then(r => r.data),
  });

  const { data: statuses = [] } = useQuery({ queryKey: ['statuses'], queryFn: () => api.get('/statuses').then(r => r.data) });
  const { data: designers = [] } = useQuery({
    queryKey: ['designers'],
    queryFn: () => api.get('/jobs/meta/designers').then(r => r.data),
  });
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: () => api.get('/clients').then(r => r.data) });

  const deleteMut = useMutation({
    mutationFn: id => api.delete(`/jobs/${id}`),
    onSuccess: () => { toast.success('Job deleted'); qc.invalidateQueries(['jobs']); qc.invalidateQueries(['kpis']); },
  });

  const archiveMut = useMutation({
    mutationFn: id => api.post(`/jobs/${id}/archive`),
    onSuccess: () => { toast.success('Job archived'); qc.invalidateQueries(['jobs']); qc.invalidateQueries(['kpis']); qc.invalidateQueries(['archived']); },
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status_id }) => api.put(`/jobs/${id}`, { status_id }),
    onSuccess: () => {
      toast.success('Status updated');
      qc.invalidateQueries(['jobs']);
      qc.invalidateQueries(['kpis']);
    },
  });

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortCol(col); setSortDir('ASC'); }
    setPage(1);
  };

  const handleFilter = (key, val) => {
    setFilters(f => ({ ...f, [key]: val }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ search: '', status_id: '', designer_id: '', client_id: '', mode: '', date_from: '', date_to: '' });
    setPage(1);
  };

  const thClass = col => `sortable${sortCol === col ? ` sort-${sortDir.toLowerCase()}` : ''}`;

  const jobs = data?.data || [];
  const total = data?.total || 0;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 14 }}>
        {[
          { label: 'Total Jobs', val: kpis?.total ?? '—', color: '#0f74b9' },
          { label: 'New job', val: kpis?.new_job ?? '—', color: '#1864ab' },
          { label: 'Due Today', val: kpis?.due_today ?? '—', color: '#6c757d' },
          { label: 'Revision', val: kpis?.revision ?? '—', color: '#c0392b' },
          { label: 'In Progress', val: kpis?.in_progress ?? '—', color: '#f9b21e' },
          { label: 'Client Review', val: kpis?.client_review ?? '—', color: '#0c5460' },
          { label: 'Over Due', val: kpis?.overdue ?? '—', color: '#b64326' },
        ].map(k => (
          <div key={k.label} style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 9, padding: '12px 14px', borderTop: `3px solid ${k.color}` }}>
            <div style={{ fontSize: 11, color: '#6c757d', marginBottom: 4 }}>{k.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#343a40' }}>{k.val}</div>
          </div>
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: 8, padding: '10px 13px', display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', marginBottom: 12 }}>
        <input className="form-group" value={filters.search} onChange={e => handleFilter('search', e.target.value)}
          placeholder="Search jobs, clients, projects…"
          style={{ flex: 1, minWidth: 150, padding: '5px 9px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12, marginBottom: 0 }} />

        <select value={filters.status_id} onChange={e => handleFilter('status_id', e.target.value)}
          style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }}>
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select value={filters.designer_id} onChange={e => handleFilter('designer_id', e.target.value)}
          style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }}>
          <option value="">All Designers</option>
          {designers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>

        <select value={filters.client_id} onChange={e => handleFilter('client_id', e.target.value)}
          style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select value={filters.mode} onChange={e => handleFilter('mode', e.target.value)}
          style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12 }}>
          <option value="">All Modes</option>
          {['Internal', 'Retainer', 'Billable', 'Non - Billable'].map(m => <option key={m}>{m}</option>)}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <input type="date" value={filters.date_from} onChange={e => handleFilter('date_from', e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12, width: 130 }} />
          <span style={{ fontSize: 11, color: '#6c757d' }}>to</span>
          <input type="date" value={filters.date_to} onChange={e => handleFilter('date_to', e.target.value)}
            style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: 5, fontSize: 12, width: 130 }} />
        </div>

        <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>Clear</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => { setEditJob(null); setDrawerOpen(true); }}>+ Add Job</button>
      </div>

      <div className="tbl-wrap">
        <div className="tbl-head-row">
          <span style={{ fontSize: 13, fontWeight: 600, color: '#343a40' }}>
            All Jobs <span style={{ fontWeight: 400, fontSize: 11, color: '#6c757d' }}>({total} records)</span>
          </span>
        </div>
        <div className="tbl-scroll">
          <table>
            <thead>
              <tr>
                {[
                  ['no', 'Job No'], ['date', 'Date'], ['client', 'Client'], ['proj', 'Project Name'],
                  ['cat', 'Category'], ['due', 'Due Date'], ['designer', 'Designer'],
                  ['status', 'Status'], ['mode', 'Mode']
                ].map(([col, lbl]) => (
                  <th key={col} className={thClass(col)} onClick={() => handleSort(col)}>
                    {lbl}<span className="sort-icon" />
                  </th>
                ))}
                <th>Invoice</th><th>Remarks</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 30, color: '#6c757d' }}>Loading…</td></tr>
              ) : jobs.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: 40, color: '#6c757d' }}>
                  No jobs match your filters.{' '}
                  <button type="button" onClick={clearFilters} style={{ color: '#0f74b9', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>Clear filters</button>
                </td></tr>
              ) : jobs.map(j => (
                <tr key={j.id} className={j.is_overdue ? 'overdue' : ''}>
                  <td><span style={{ fontWeight: 700, color: j.is_overdue ? '#b64326' : '#0f74b9', fontFamily: 'monospace', fontSize: 11 }}>{j.job_no}</span></td>
                  <td style={{ color: '#6c757d', fontSize: 11, whiteSpace: 'nowrap' }}>{j.created_at ? format(new Date(j.created_at), 'dd MMM yyyy') : '—'}</td>
                  <td><strong>{j.client_name}</strong></td>
                  <td style={{ maxWidth: 130, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.project_name}>{j.project_name}</td>
                  <td><span style={{ fontSize: 10, background: '#f1f3f5', padding: '2px 7px', borderRadius: 10, color: '#6c757d' }}>{j.category_name}</span></td>
                  <td style={{ whiteSpace: 'nowrap', fontSize: 11, color: j.is_overdue ? '#b64326' : 'inherit', fontWeight: j.is_overdue ? 600 : 400 }}>
                    {j.due_date ? format(new Date(j.due_date), 'dd MMM yyyy') : '—'}{j.is_overdue ? ' ▲' : ''}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{j.designer_name || '—'}</td>
                  <td>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <select
                        value={j.status_id}
                        onChange={(e) => statusMut.mutate({ id: j.id, status_id: e.target.value })}
                        disabled={statusMut.isPending}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                          zIndex: 1
                        }}
                      >
                        {statuses.map(s => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                      <Badge type="status" value={j.status_name} />
                    </div>
                  </td>
                  <td><Badge type="mode" value={j.mode} /></td>
                  <td style={{ color: '#6c757d', fontSize: 11 }}>{j.invoice_no || '—'}</td>
                  <td style={{ color: '#6c757d', maxWidth: 90, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.remarks}>{j.remarks || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" className="ico-btn" onClick={() => { setEditJob(j); setDrawerOpen(true); }} title="Edit">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M11 2l3 3-9 9H2v-3l9-9z" /></svg>
                      </button>
                      {j.status_name === 'Completed' && ['super_admin', 'manager'].includes(user.role) && (
                        <button type="button" className="ico-btn" onClick={() => setArchiveId(j.id)} title="Archive" style={{ borderColor: '#037b3e' }}>
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#037b3e" strokeWidth="1.5"><rect x="1" y="4" width="14" height="10" rx="1" /><path d="M1 4l2-3h10l2 3" /><line x1="6" y1="8" x2="10" y2="8" /></svg>
                        </button>
                      )}
                      {user.role === 'super_admin' && (
                        <button type="button" className="ico-btn" onClick={() => setDeleteId(j.id)} title="Delete">
                          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#dc3545" strokeWidth="1.5"><polyline points="2,4 14,4" /><path d="M5 4V2h6v2M6 7v5M10 7v5" /><rect x="3" y="4" width="10" height="10" rx="1" /></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          page={page} totalPages={Math.max(1, Math.ceil(total / perPage))}
          total={total} perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={n => { setPerPage(n); setPage(1); }}
        />
      </div>

      <JobDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditJob(null); }}
        job={editJob}
        statuses={statuses}
        designers={designers}
        clients={clients}
        onSaved={() => { qc.invalidateQueries(['jobs']); qc.invalidateQueries(['kpis']); }}
      />

      <ConfirmModal
        open={!!deleteId} onClose={() => setDeleteId(null)}
        title="Delete this job?" message="This action is permanent and cannot be undone."
        confirmLabel="Delete" danger
        onConfirm={() => deleteMut.mutate(deleteId)}
      />

      <ConfirmModal
        open={!!archiveId} onClose={() => setArchiveId(null)}
        title="Archive this job?" message="Completed jobs will be moved to Archive. They can still be viewed but not edited."
        confirmLabel="Archive" danger={false}
        onConfirm={() => archiveMut.mutate(archiveId)}
      />
    </div>
  );
}
